import { createFileRoute, notFound, Outlet, redirect } from '@tanstack/react-router';
import { SharedConfig } from '@_/config/shared-config.ts';
import { LocaleCodes } from '@_/static';
import { getCanonicalLocaleCode, getClosestLocale } from '@_/utils';
import { App } from '#src/App.tsx';
import { getCookie } from '#src/functions/getCookie.ts';
import { getPreferredLanguage } from '#src/functions/getPreferredLanguage.ts';
import { loadI18nMessages } from '#src/utils/i18nMessageUtil.ts';

export const Route = createFileRoute('/{-$locale}')({
  component: RouteComponent,
  beforeLoad: async ({ location, params }) => {
    if (!params.locale) {
      let targetLocale: string | undefined;

      // Use saved locale from cookies if it exists
      const cookieLocale = getCookie(SharedConfig.cookies.locale);
      targetLocale = getClosestLocale(cookieLocale, LocaleCodes);

      // Fallback to user's preferred language
      if (!targetLocale) {
        const preferredLanguage = getPreferredLanguage();
        targetLocale = getClosestLocale(preferredLanguage, LocaleCodes);
      }

      // Fallback to default locale
      if (!targetLocale) {
        targetLocale = SharedConfig.defaultLocale;
      }

      // Redirect to localized URL
      throw redirect({
        href: `/${targetLocale}${location.href}`,
        replace: true,
        statusCode: 302
      });
    }

    if (!isValidLocale(params.locale)) {
      throw notFound();
    }

    const locale = getCanonicalLocaleCode(params.locale);

    return { locale };
  },
  loader: async ({ context }) => {
    const { locale } = context;
    const i18nMessages = await loadI18nMessages(locale, SharedConfig.defaultLocale);
    return { i18nMessages, locale };
  },
  ssr: true
});

function RouteComponent() {
  const { i18nMessages, locale } = Route.useLoaderData();

  return (
    <App locale={locale} i18nMessages={i18nMessages}>
      <Outlet />
    </App>
  );
}

function isValidLocale(value: string): boolean {
  let locale: string;

  try {
    locale = getCanonicalLocaleCode(value);
  } catch (error) {
    locale = value;
  }

  return LocaleCodes.includes(locale as (typeof LocaleCodes)[number]);
}
